import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { PasswordResetFormData } from '../types/auth';
import { validateEmail } from '../utils/validation';
import './PasswordResetForm.css';

interface PasswordResetFormProps {
  returnUrl?: string;
  onSuccess?: () => void;
  onBackToLogin?: () => void;
}

/**
 * Password Reset Form Component
 * Provides password reset functionality with Firebase integration
 */
export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ 
  returnUrl, 
  onSuccess, 
  onBackToLogin 
}) => {
  const { t } = useTranslation();
  const { authState, resetPassword, clearError } = useAuth();
  const [formData, setFormData] = useState<PasswordResetFormData>({
    email: ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Clear errors when form data changes
  useEffect(() => {
    if (authState.error) {
      clearError();
    }
  }, [formData.email, clearError, authState.error]);

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      errors.email = t(emailValidation.errorKey ?? 'validation.fallback.invalidEmail', emailValidation.params);
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle input field changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear success state when user starts typing again
    if (isSuccess) {
      setIsSuccess(false);
      setSuccessMessage('');
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword(formData.email);
      
      // If no error occurred, show success message
      if (!authState.error) {
        setIsSuccess(true);
        setSuccessMessage(t('passwordReset.successMessage', { email: formData.email }));
        
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      // Error handling is managed by the useAuth hook
      console.error('Password reset error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle back to login click
   */
  const handleBackToLogin = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    if (onBackToLogin) {
      onBackToLogin();
    } else {
      // Default navigation back to login
      window.location.href = `/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`;
    }
  };

  /**
   * Handle try again (reset form)
   */
  const handleTryAgain = () => {
    setIsSuccess(false);
    setSuccessMessage('');
    setFormData({ email: '' });
    setValidationErrors({});
    clearError();
  };

  if (isSuccess) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-success">
          <div className="password-reset-success__icon">
            ✓
          </div>
          <h1 className="password-reset-success__title">{t('passwordReset.successHeading')}</h1>
          <p className="password-reset-success__message">
            {successMessage}
          </p>
          <div className="password-reset-success__actions">
            <button
              type="button"
              onClick={handleTryAgain}
              className="password-reset-success__button password-reset-success__button--secondary"
            >
              {t('passwordReset.sendAnother')}
            </button>
            <a
              href="#"
              onClick={handleBackToLogin}
              className="password-reset-success__button password-reset-success__button--primary"
            >
              {t('passwordReset.backToSignIn')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <form className="password-reset-form" onSubmit={handleSubmit} noValidate>
        <h1 className="password-reset-form__title">{t('passwordReset.title')}</h1>
        <p className="password-reset-form__description">
          {t('passwordReset.description')}
        </p>

        {authState.error && (
          <div className="password-reset-form__error" role="alert" aria-live="polite">
            {t(authState.error)}
          </div>
        )}

        <div className="password-reset-form__field">
          <label htmlFor="email" className="password-reset-form__label">
            {t('passwordReset.emailLabel')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`password-reset-form__input ${validationErrors.email ? 'password-reset-form__input--error' : ''}`}
            placeholder={t('passwordReset.emailPlaceholder')}
            required
            autoComplete="email"
            aria-describedby={validationErrors.email ? 'email-error' : undefined}
            disabled={isSubmitting || authState.loading}
            autoFocus
          />
          {validationErrors.email && (
            <div id="email-error" className="password-reset-form__field-error" role="alert">
              {validationErrors.email}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="password-reset-form__submit"
          disabled={isSubmitting || authState.loading}
        >
          {isSubmitting || authState.loading ? t('passwordReset.submitting') : t('passwordReset.submit')}
        </button>

        <div className="password-reset-form__links">
          <a
            href="#"
            onClick={handleBackToLogin}
            className="password-reset-form__link"
          >
            {t('passwordReset.backToSignInArrow')}
          </a>
        </div>

        {returnUrl && (
          <div className="password-reset-form__return-info">
            <small>{t('passwordReset.redirectNote')}</small>
          </div>
        )}
      </form>
    </div>
  );
};